import { buildCoderMap } from "@saberhq/anchor-contrib";
import { PublicKey } from "@solana/web3.js";

import { CremaPositionWrapperJSON } from "./idls/crema_position_wrapper";
import { QuarryMineJSON } from "./idls/quarry_mine";
import { QuarryMintWrapperJSON } from "./idls/quarry_mint_wrapper";
import { QuarryRedeemerJSON } from "./idls/quarry_redeemer";
import type {
  MineProgram,
  MineTypes,
  MintWrapperProgram,
  MintWrapperTypes,
  QuarryMergeMineProgram,
  QuarryMergeMineTypes,
  QuarryOperatorProgram,
  QuarryOperatorTypes,
  TransferWrapperProgram,
  TransferWrapperTypes,
} from "./programs";
import {
  ActivityJSON,
  QuarryMergeMineJSON,
  QuarryOperatorJSON,
  TransferWrapperJSON,
} from "./programs";
import type { ActivityProgram, ActivityTypes } from "./programs/activity";
import type {
  PositionWrapperProgram,
  PositionWrapperTypes,
} from "./programs/positionWrapper";
import type { RedeemerProgram, RedeemerTypes } from "./programs/redeemer";
import type { RegistryProgram, RegistryTypes } from "./programs/registry";
import { QuarryRegistryJSON } from "./programs/registry";

/**
 * Types of all programs.
 */
export interface Programs {
  MergeMine: QuarryMergeMineProgram;
  Mine: MineProgram;
  MintWrapper: MintWrapperProgram;
  Operator: QuarryOperatorProgram;
  Redeemer: RedeemerProgram;
  Registry: RegistryProgram;
  PositionWrapper: PositionWrapperProgram;
  Activity: ActivityProgram;
  TransferWrapper: TransferWrapperProgram;
}

/**
 * Quarry program addresses.
 */
export const QUARRY_ADDRESSES = {
  MergeMine: new PublicKey("CMMY6MZ63T4gt99ESp5dVGDZKwfQUjGkKGTsbj85E9FV"),
  Mine: new PublicKey("CMNheuZrtZ8D7CK25GdaW4krf5Vu859ReDfhhfq2XSkz"),
  MintWrapper: new PublicKey("CMWr5a5feRxAeowwHcm1u5bYwmUvsAjcBbqLRcBshyAR"),
  Operator: new PublicKey("CoPU62racfQrpraE4Nfket2aVeNDfDbvHXmTQeeDdy1k"),
  Redeemer: new PublicKey("CRDekeFLD2gWmEVdHiwLRfYwmCgMWpPKFCBTzx2GSpv1"),
  Registry: new PublicKey("CReGJGRWU3hFby2Vn1UNUx3Yao8WkVKvfENChtjHDVow"),
  PositionWrapper: new PublicKey(
    "CPWdCBwzgC2MNQKz7AGAkZH51BskgA1LY9v8RPikQ2x1"
  ),
  CremaSwap: new PublicKey("6MLxLqiXaaSUpkgMnWDTuejNZEz3kE7k2woyHGVFw319"),
  Activity: new PublicKey("ACTj4dCtGJqDsy7TJ5C3TDbjbeA3nzoaLW9LW571MzeU"),
  TransferWrapper: new PublicKey(
    "CTWrBmckXuRs89bwFBebh67nvv1kHpiFPNYe1o3JnSKa"
  ),
};

/**
 * Quarry program IDLs.
 */
export const QUARRY_IDLS = {
  MergeMine: QuarryMergeMineJSON,
  Mine: QuarryMineJSON,
  MintWrapper: QuarryMintWrapperJSON,
  Operator: QuarryOperatorJSON,
  Redeemer: QuarryRedeemerJSON,
  Registry: QuarryRegistryJSON,
  PositionWrapper: CremaPositionWrapperJSON,
  Activity: ActivityJSON,
  TransferWrapper: TransferWrapperJSON,
};

/**
 * Quarry program IDLs.
 */
export const QUARRY_CODERS = buildCoderMap<{
  MergeMine: QuarryMergeMineTypes;
  Mine: MineTypes;
  MintWrapper: MintWrapperTypes;
  Operator: QuarryOperatorTypes;
  Redeemer: RedeemerTypes;
  Registry: RegistryTypes;
  PositionWrapper: PositionWrapperTypes;
  Activity: ActivityTypes;
  TransferWrapper: TransferWrapperTypes;
}>(QUARRY_IDLS, QUARRY_ADDRESSES);

/**
 * Recipient of protocol fees.
 */
export const QUARRY_FEE_TO = new PublicKey(
  "Cmfee22rZSWnaWABHcLaDAr7FsFMz7wB35J4YES9q5u4"
);

/**
 * Sets the protocol fees.
 */
export const QUARRY_FEE_SETTER = new PublicKey(
  "Cmfee22rZSWnaWABHcLaDAr7FsFMz7wB35J4YES9q5u4"
);

/**
 * The CremaSwap position key size
 */
export const CREMA_POSITION_ACCOUNT_SIZE = 360000;
